/**
 * Calculator — lightweight standard/scientific calculator. State is purely
 * local; history is kept in component state (not persisted). Keyboard-friendly.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Backspace, ArrowsClockwise, ClockCounterClockwise } from "@phosphor-icons/react";

// Safe expression evaluator — pure recursive-descent parser (no eval/Function).
// Grammar:  expr = term (('+'|'-') term)*
//           term = factor (('*'|'/') factor)*
//           factor = number | '(' expr ')' | ('+'|'-') factor | factor '%'
function evaluate(expr) {
  // normalise visual operators to ASCII
  const src = String(expr || "")
    .replace(/×/g, "*").replace(/÷/g, "/").replace(/−/g, "-");
  if (!src.trim()) return "";

  // Tokenise
  const tokens = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (ch === " " || ch === "\t") { i++; continue; }
    if ("+-*/()%".includes(ch)) { tokens.push({ t: ch }); i++; continue; }
    if ((ch >= "0" && ch <= "9") || ch === ".") {
      let j = i;
      while (j < src.length && ((src[j] >= "0" && src[j] <= "9") || src[j] === ".")) j++;
      const num = Number(src.slice(i, j));
      if (!isFinite(num)) throw new Error("Invalid number");
      tokens.push({ t: "n", v: num });
      i = j;
      continue;
    }
    throw new Error("Invalid characters");
  }

  let pos = 0;
  const peek = () => tokens[pos]?.t;
  const eat = (t) => { if (peek() !== t) throw new Error("Parse error"); return tokens[pos++]; };

  function parseFactor() {
    let sign = 1;
    while (peek() === "+" || peek() === "-") {
      if (tokens[pos++].t === "-") sign = -sign;
    }
    let val;
    if (peek() === "(") { eat("("); val = parseExpr(); eat(")"); }
    else if (peek() === "n") { val = tokens[pos++].v; }
    else throw new Error("Parse error");
    // trailing "%" — treat as divide-by-100
    while (peek() === "%") { pos++; val = val / 100; }
    return sign * val;
  }
  function parseTerm() {
    let v = parseFactor();
    while (peek() === "*" || peek() === "/") {
      const op = tokens[pos++].t;
      const rhs = parseFactor();
      if (op === "/") { if (rhs === 0) throw new Error("Division by zero"); v = v / rhs; }
      else v = v * rhs;
    }
    return v;
  }
  function parseExpr() {
    let v = parseTerm();
    while (peek() === "+" || peek() === "-") {
      const op = tokens[pos++].t;
      const rhs = parseTerm();
      v = op === "+" ? v + rhs : v - rhs;
    }
    return v;
  }

  const result = parseExpr();
  if (pos !== tokens.length) throw new Error("Trailing input");
  if (typeof result !== "number" || !isFinite(result)) throw new Error("Math error");
  return Number(result.toPrecision(12)).toString();
}

const KEYS = [
  ["C", "(", ")", "÷"],
  ["7", "8", "9", "×"],
  ["4", "5", "6", "−"],
  ["1", "2", "3", "+"],
  ["0", ".", "%", "="],
];

export default function Calculator() {
  const [expr, setExpr] = useState("");
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState("");

  const compute = useCallback(() => {
    try {
      const r = evaluate(expr);
      if (r === "") return;
      setHistory((h) => [{ expr, result: r, ts: new Date().toISOString() }, ...h].slice(0, 50));
      setExpr(r);
      setErr("");
    } catch (e) {
      setErr(e.message);
    }
  }, [expr]);

  const press = useCallback((k) => {
    setErr("");
    if (k === "C") { setExpr(""); return; }
    if (k === "=") { compute(); return; }
    setExpr((prev) => prev + k);
  }, [compute]);

  const backspace = () => setExpr((p) => p.slice(0, -1));

  // Keyboard support
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter") { e.preventDefault(); compute(); return; }
      if (e.key === "Backspace") { e.preventDefault(); backspace(); return; }
      if (e.key === "Escape") { setExpr(""); return; }
      if (/^[0-9+\-*/().%]$/.test(e.key)) { setExpr((p) => p + e.key); return; }
      if (e.key === "x" || e.key === "X") setExpr((p) => p + "*");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [compute]);

  return (
    <div className="flex flex-col h-full bg-white" data-testid="calculator-panel">
      {/* Display */}
      <div className="p-4 border-b border-[var(--border)] bg-[var(--bg-elev)]">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]" data-testid="calc-history-preview">
          {history[0] ? `${history[0].expr} = ${history[0].result}` : "—"}
        </div>
        <div className="text-right text-2xl font-mono mt-1 min-h-[2rem] break-all" data-testid="calc-display">
          {expr || "0"}
        </div>
        {err && <div className="text-right text-xs text-red-600 mt-1" data-testid="calc-error">{err}</div>}
      </div>

      {/* Keys */}
      <div className="p-3 grid grid-cols-4 gap-1.5 flex-1">
        {KEYS.flat().map((k) => (
          <button
            key={k}
            onClick={() => press(k)}
            className={`py-3 text-base font-mono border border-[var(--border)] hover:bg-[var(--bg-elev)] transition-colors ${
              k === "=" ? "bg-[var(--accent)] text-white border-[var(--accent)] hover:opacity-90" : ""
            } ${["÷", "×", "−", "+"].includes(k) ? "bg-[var(--bg-elev)] font-medium" : ""}`}
            data-testid={`calc-key-${k}`}
          >
            {k}
          </button>
        ))}
        <button onClick={backspace} className="col-span-4 py-2 border border-[var(--border)] text-xs font-mono flex items-center justify-center gap-1 hover:bg-[var(--bg-elev)]" data-testid="calc-backspace">
          <Backspace size={14} /> Backspace
        </button>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="border-t border-[var(--border)] max-h-32 overflow-y-auto">
          <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] flex items-center justify-between border-b border-[var(--border)] bg-[var(--bg-elev)]">
            <span className="flex items-center gap-1"><ClockCounterClockwise size={11} /> History</span>
            <button onClick={() => setHistory([])} className="hover:text-[var(--text-primary)]" data-testid="calc-clear-history">
              <ArrowsClockwise size={11} />
            </button>
          </div>
          {history.map((h, i) => (
            <button
              key={i}
              onClick={() => setExpr(h.result)}
              className="block w-full px-3 py-1.5 text-xs font-mono text-left hover:bg-[var(--bg-elev)] border-b border-[var(--border)]"
              data-testid={`calc-history-${i}`}
            >
              <span className="text-[var(--text-muted)]">{h.expr}</span>
              <span className="text-[var(--text-primary)] ml-2">= {h.result}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
